import { useTranslation } from 'react-i18next'
import { referenceMentionSections } from './referenceMentionSections'
import { referenceInteractionLog } from '../../CanvasReferencePicking'
/**
 * RichTextBox — contentEditable text box with inline @ mention chips.
 *
 * Provides a shared rich text component that replaces plain <textarea>
 * inputs across the canvas. Supports inline @ mention chips with a
 * dropdown, keyboard navigation, `@[[nodeId]]` serialization, and
 * an imperative handle for programmatic control.
 *
 * Fixes the cursor-jump-to-end bug present in V1 textarea inputs.
 */

import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useNodes } from 'reactflow'
import type {
  ReactFlowCanvasNode,
  ReactFlowCanvasNodeData,
} from '../../../../hooks/useYCanvas'
import { buildCandidates, type MentionCandidate } from './mentionUtils'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'
import {
  createMentionChipElement,
  serializeNodeText,
  getPlainTextFromEditor,
  getReferencedNodeIdsFromEditor,
  selectionInsideEditor,
  placeCaretAtEnd,
  hydrateContent,
  reconcileMentionChips,
  DropdownItem,
} from '../../../chat/contentEditableUtils'

const mentionLog = createDebug('dw:mention-trigger:richtextbox')
const enterKeyLog = createNadDebug('dw:richtextbox-enter-key:keyboard')
const canvasReferenceLog = createNadDebug('dw:canvas:motion-prompt-references')
const richTextSerializationLog = createNadDebug('dw:canvas:rich-text-serialization')

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RichTextBoxState {
  plainText: string
  referencedNodeIds: string[]
}

export interface RichTextBoxHandle {
  clear: () => void
  focus: () => void
  setText: (text: string) => void
  getPlainText: () => string
  getReferencedNodeIds: () => string[]
  insertAtCursor: (text: string) => void
  wrapSelection: (before: string, after: string) => void
}

export interface RichTextBoxProps {
  /** Attached references in strip order, independent of the prompt. */
  referenceNodeIds?: readonly string[]
  /** Canvas nodes to build mention candidates from */
  canvasNodes?: ReactFlowCanvasNode[]
  /** Canvas node that owns this editor, used only for structural NAD logs. */
  referenceOwnerNodeId?: string
  /** Canvas node type that owns this editor, used only for structural NAD logs. */
  referenceOwnerNodeType?: string
  /** Whether the text box is disabled */
  disabled?: boolean
  /** Placeholder text shown when empty */
  placeholder?: string
  /** Raw text containing @[[nodeId]] markers to hydrate into chips on mount */
  initialContent?: string
  /** Called when content changes */
  onContentChange?: (state: RichTextBoxState) => void
  /** Called when Enter (without Shift) is pressed (non-dropdown context) */
  onSubmit?: () => void
  /** Whether Enter without Shift should submit when onSubmit is provided. Default: true. */
  submitOnEnter?: boolean
  /** Whether Shift+Enter should submit when onSubmit is provided. Default: false. */
  submitOnShiftEnter?: boolean
  /** Called when Escape is pressed (non-dropdown context) */
  onEscape?: () => void
  /**
   * Called when Cmd/Ctrl+Z is pressed (non-dropdown context). When provided,
   * the shortcut is intercepted and `onUndo` is invoked instead of falling
   * through to the browser's native contentEditable undo. Useful for callers
   * that mutate the editor via `setText()` (innerHTML), which bypasses the
   * native undo stack.
   */
  onUndo?: () => void
  /** Called when a mention chip is clicked (to pan/zoom canvas to that node) */
  onMentionClick?: (nodeId: string) => void
  /** Additional CSS classes for the editor div */
  className?: string
  /** Additional CSS classes for the outer border container */
  containerClassName?: string
  /** data-testid for the outer border container */
  containerTestId?: string
  /** data-testid for the contentEditable editor */
  editorTestId?: string
  /** Min height in px. Default: 24 */
  minHeight?: number
  /** Max height in px. Default: 160 */
  maxHeight?: number
  /** Max character count for built-in counter. Default: 1000. Set to 0 to hide counter. */
  maxLength?: number
  /** Background class for the built-in character counter. Default: 'bg-surface-2'.
   * Override when the surrounding container uses a different background, so the
   * counter blends in instead of producing a visible block.
   */
  counterClassName?: string
  /** When true, skip adjustHeight and let flex layout size the editor. */
  flexFill?: boolean
}

export interface CanvasRichTextBoxProps
  extends Omit<
    RichTextBoxProps,
    'canvasNodes' | 'referenceOwnerNodeId' | 'referenceOwnerNodeType'
  > {
  /** Canvas node that owns this editor. */
  referenceOwnerNodeId: string
  /** Canvas node type that owns this editor. */
  referenceOwnerNodeType: string
}

// ─── RichTextBox Component ───────────────────────────────────────────────────

const RichTextBoxInner = forwardRef<RichTextBoxHandle, RichTextBoxProps>(
  function RichTextBox(
    {
      canvasNodes,
      referenceNodeIds,
      referenceOwnerNodeId,
      referenceOwnerNodeType,
      disabled = false,
      placeholder = 'Type a message...',
      initialContent,
      onContentChange,
      onSubmit,
      submitOnEnter = true,
      submitOnShiftEnter = false,
      onEscape,
      onUndo,
      onMentionClick,
      className = '',
      containerClassName,
      containerTestId,
      editorTestId = 'rich-text-editor',
      minHeight = 24,
      maxHeight = 160,
      maxLength = 1000,
      counterClassName = 'bg-surface-2',
      flexFill = false,
    },
    ref
  ) {
    const { t } = useTranslation('canvas')
    const projectId = useOptionalProjectId()
    const editorRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const anchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
    const composingRef = useRef(false)
    const initialContentHydratedRef = useRef(false)
    const onContentChangeRef = useRef(onContentChange)
    const lastLineStructureRef = useRef<string | null>(null)
    onContentChangeRef.current = onContentChange

    const [showDropdown, setShowDropdown] = useState(false)
    const [filterText, setFilterText] = useState('')
    const [highlightIndex, setHighlightIndex] = useState(0)
    const [textLength, setTextLength] = useState(0)
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null)

    const candidates = useMemo(
      () => (canvasNodes ? buildCandidates(canvasNodes) : []),
      [canvasNodes]
    )

    const lastCanvasReferenceStateRef = useRef<string | null>(null)

    useEffect(() => {
      if (
        !canvasReferenceLog.enabled ||
        !referenceOwnerNodeId ||
        !referenceOwnerNodeType
      ) {
        return
      }

      const referencedNodeIds: string[] = []
      const seen = new Set<string>()
      const markerPattern = /@\[\[([^\]]+)\]\]/g
      const content = initialContent ?? ''
      let match: RegExpExecArray | null
      while ((match = markerPattern.exec(content)) !== null) {
        const nodeId = match[1]
        if (!seen.has(nodeId)) {
          seen.add(nodeId)
          referencedNodeIds.push(nodeId)
        }
      }

      const candidateIds = new Set(candidates.map((candidate) => candidate.id))
      const referencedNodeSignature = referencedNodeIds.join(',')
      const missingNodeSignature = referencedNodeIds
        .filter((nodeId) => !candidateIds.has(nodeId))
        .join(',')
      const canvasNodeCount = canvasNodes?.length ?? 0
      const candidateCount = candidates.length
      const state = [
        canvasNodeCount,
        candidateCount,
        referencedNodeSignature,
        missingNodeSignature,
      ].join('\u0000')
      if (lastCanvasReferenceStateRef.current === state) return
      lastCanvasReferenceStateRef.current = state

      canvasReferenceLog({
        event: 'reference-text-box-resolution',
        trace_id: `reference-text-box:${referenceOwnerNodeId}`,
        node_id: referenceOwnerNodeId,
        node_type: referenceOwnerNodeType,
        canvas_node_count: canvasNodeCount,
        candidate_count: candidateCount,
        referenced_node_ids: referencedNodeSignature,
        missing_node_ids: missingNodeSignature,
      })
    }, [
      candidates,
      canvasNodes?.length,
      initialContent,
      referenceOwnerNodeId,
      referenceOwnerNodeType,
    ])

    const mentionSections = useMemo(() => referenceMentionSections(candidates, referenceNodeIds, filterText), [candidates, referenceNodeIds, filterText])
    const filteredCandidates = mentionSections.candidates
    useEffect(() => {
      if (!showDropdown) return
      setHighlightIndex(0)
      referenceInteractionLog({
        event: 'mention:sections',
        project_id: projectId,
        owner_node_id: referenceOwnerNodeId,
        filter_query: filterText,
        grouping_mode: mentionSections.sectioned ? 'references_then_canvas' : 'flat_canvas',
        reference_count: mentionSections.referenceCount,
        canvas_count: filteredCandidates.length - mentionSections.referenceCount,
        reference_ids: JSON.stringify(filteredCandidates.slice(0, mentionSections.referenceCount).map(candidate => candidate.id)),
        canvas_ids: JSON.stringify(filteredCandidates.slice(mentionSections.referenceCount).map(candidate => candidate.id)),
        sectioned: mentionSections.sectioned,
      })
    }, [showDropdown, filterText, mentionSections, filteredCandidates, referenceOwnerNodeId, projectId])

    // ── Editor utilities ──

    const adjustHeight = useCallback(() => {
      if (flexFill) return
      const editor = editorRef.current
      if (!editor) return
      editor.style.height = 'auto'
      const nextHeight = Math.min(Math.max(editor.scrollHeight, minHeight), maxHeight)
      editor.style.height = `${nextHeight}px`
    }, [flexFill, minHeight, maxHeight])

    // Re-fit the height when the box is resized horizontally (e.g. a motion
    // timeline zoom narrows a block), or wrapped text overflows the stale
    // height. Height-only changes — including our own — are ignored.
    useEffect(() => {
      if (flexFill) return
      const editor = editorRef.current
      if (!editor || typeof ResizeObserver === 'undefined') return
      let lastWidth = editor.clientWidth
      const observer = new ResizeObserver(() => {
        const width = editor.clientWidth
        if (width === lastWidth) return
        lastWidth = width
        adjustHeight()
      })
      observer.observe(editor)
      return () => observer.disconnect()
    }, [flexFill, adjustHeight])

    const emitContentState = useCallback(() => {
      const editor = editorRef.current
      const plainText = getPlainTextFromEditor(editor)
      const state: RichTextBoxState = {
        plainText,
        referencedNodeIds: getReferencedNodeIdsFromEditor(editor),
      }
      if (editor && richTextSerializationLog.enabled) {
        const newlineCount = plainText.split('\n').length - 1
        const topLevelBlockCount = Array.from(editor.children).filter(
          (child) => child.tagName === 'DIV' || child.tagName === 'P',
        ).length
        const brCount = editor.querySelectorAll('br').length
        const lineStructure = `${newlineCount}:${topLevelBlockCount}:${brCount}`
        if (lastLineStructureRef.current !== lineStructure) {
          lastLineStructureRef.current = lineStructure
          richTextSerializationLog({
            event: 'rich-text-line-structure-serialized',
            trace_id: referenceOwnerNodeId
              ? `rich-text-serialization:${referenceOwnerNodeId}`
              : undefined,
            node_id: referenceOwnerNodeId,
            node_type: referenceOwnerNodeType,
            editor_test_id: editorTestId,
            container_test_id: containerTestId,
            text_length: plainText.length,
            newline_count: newlineCount,
            top_level_block_count: topLevelBlockCount,
            br_count: brCount,
          })
        }
      }
      setTextLength(plainText.length)
      onContentChangeRef.current?.(state)
      return state
    }, [containerTestId, editorTestId, referenceOwnerNodeId, referenceOwnerNodeType])

    const closeDropdown = useCallback(() => {
      anchorRef.current = null
      setShowDropdown(false)
      setFilterText('')
      setHighlightIndex(0)
      setDropdownPos(null)
    }, [])

    // ── Mention trigger detection ──

    const syncMentionTrigger = useCallback(() => {
      const editor = editorRef.current
      if (!editor || candidates.length === 0 || composingRef.current) {
        closeDropdown()
        return
      }

      const selection = window.getSelection()
      if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
        closeDropdown()
        return
      }

      const range = selection.getRangeAt(0)
      const textNode = range.startContainer
      if (textNode.nodeType !== Node.TEXT_NODE) {
        closeDropdown()
        return
      }

      const text = textNode.textContent ?? ''
      const cursorPos = range.startOffset

      let atPos = -1
      for (let i = cursorPos - 1; i >= 0; i--) {
        const char = text[i]
        if (char === '@') {
          atPos = i
          break
        }
        if (char === '\n' || char === '\r') break
      }

      mentionLog('syncMentionTrigger text=%s cursorPos=%d atPos=%d charBefore=%s', text, cursorPos, atPos, atPos > 0 ? text[atPos - 1] : '(none)')

      if (atPos >= 0) {
        anchorRef.current = { node: textNode, offset: atPos }
        const query = text.substring(atPos + 1, cursorPos)
        setFilterText(query)
        setShowDropdown(true)
        setHighlightIndex(0)

        // Compute dropdown position from editor bounding rect
        const editorRect = editor.getBoundingClientRect()
        setDropdownPos({
          top: editorRect.top,
          left: editorRect.left,
          width: editorRect.width,
        })

        // If no candidates match, close the dropdown
        const queryLower = query.toLowerCase()
        const hasMatch = queryLower === '' || candidates.some(
          (c) => c.label.toLowerCase().includes(queryLower) || c.type.toLowerCase().includes(queryLower)
        )
        if (!hasMatch) {
          closeDropdown()
        }
        return
      }

      closeDropdown()
    }, [candidates, closeDropdown])

    // ── Chip operations ──

    const removeChip = useCallback(
      (chip: HTMLElement) => {
        chip.remove()
        const editor = editorRef.current
        if (editor && editor.textContent === '') {
          editor.innerHTML = ''
        }
        emitContentState()
        adjustHeight()
        if (editor) placeCaretAtEnd(editor)
      },
      [adjustHeight, emitContentState]
    )

    const insertMention = useCallback(
      (candidate: MentionCandidate) => {
        const editor = editorRef.current
        const anchor = anchorRef.current
        if (!editor || !anchor) {
          closeDropdown()
          return
        }

        const anchorNode = anchor.node
        if (anchorNode.nodeType !== Node.TEXT_NODE || !anchorNode.parentNode) {
          closeDropdown()
          return
        }

        const fullText = anchorNode.textContent ?? ''
        const mentionLength = 1 + filterText.length
        const before = fullText.substring(0, anchor.offset)
        const after = fullText.substring(anchor.offset + mentionLength)
        const parent = anchorNode.parentNode

        const beforeNode = document.createTextNode(before)
        const chip = createMentionChipElement(candidate, projectId)
        const trailingSpacer = after.length === 0 || /^\s/.test(after) ? '' : ' '
        const afterNode = document.createTextNode(`${trailingSpacer}${after}`)

        parent.insertBefore(beforeNode, anchorNode)
        parent.insertBefore(chip, anchorNode)
        parent.insertBefore(afterNode, anchorNode)
        parent.removeChild(anchorNode)

        // Place cursor after chip (no jump-to-end)
        const selection = window.getSelection()
        if (selection) {
          const range = document.createRange()
          range.setStart(afterNode, trailingSpacer.length)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
        }

        emitContentState()
        adjustHeight()
        closeDropdown()
        editor.focus()
      },
      [adjustHeight, closeDropdown, emitContentState, filterText, projectId]
    )

    // ── Input/key handlers ──

    const handleInput = useCallback(() => {
      if (composingRef.current) return
      initialContentHydratedRef.current = true
      emitContentState()
      adjustHeight()
      syncMentionTrigger()
    }, [adjustHeight, emitContentState, syncMentionTrigger])

    const handleCompositionStart = useCallback(() => {
      composingRef.current = true
    }, [])

    const handleCompositionEnd = useCallback(() => {
      composingRef.current = false
      // After composition ends, check for @ trigger
      handleInput()
    }, [handleInput])

    // ── Shared serialization helper for copy/cut ──

    const serializeSelection = useCallback((): string | null => {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null

      const range = selection.getRangeAt(0)
      const fragment = range.cloneContents()
      const container = document.createElement('div')
      container.appendChild(fragment)

      const serialized = serializeNodeText(container, container, {
        selectionFragment: true,
      })
      return serialized || null
    }, [])

    // ── Copy handler: serialize chips as @[[nodeId]] ──

    const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
      const serialized = serializeSelection()
      if (!serialized) return

      e.preventDefault()
      e.clipboardData.setData('text/plain', serialized)
    }, [serializeSelection])

    // ── Cut handler: serialize chips then delete selected content ──

    const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
      const serialized = serializeSelection()
      if (!serialized) return

      e.preventDefault()
      e.clipboardData.setData('text/plain', serialized)

      // Delete the selected content
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        selection.getRangeAt(0).deleteContents()
      }

      // Clean up empty editor state
      const editor = editorRef.current
      if (editor && editor.textContent === '') {
        editor.innerHTML = ''
      }

      emitContentState()
      adjustHeight()
    }, [adjustHeight, emitContentState, serializeSelection])

    // ── Paste handler: hydrate @[[nodeId]] markers into chips ──

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault()

        const editor = editorRef.current
        if (!editor) return

        const text = e.clipboardData.getData('text/plain')
        if (!text) return

        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return

        const range = selection.getRangeAt(0)
        if (!range.collapsed) {
          range.deleteContents()
        }

        const MARKER_RE = /@\[\[([^\]]+)\]\]/g
        const hasMarkers = MARKER_RE.test(text)

        if (hasMarkers) {
          // Build a document fragment with chips hydrated from markers
          MARKER_RE.lastIndex = 0
          const frag = document.createDocumentFragment()
          let lastIndex = 0
          let match: RegExpExecArray | null

          while ((match = MARKER_RE.exec(text)) !== null) {
            if (match.index > lastIndex) {
              frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
            }
            const nodeId = match[1]
            const candidate = candidates.find((c) => c.id === nodeId)
            if (candidate) {
              frag.appendChild(createMentionChipElement(candidate, projectId))
            } else {
              // Unknown node — render fallback chip with muted style
              const fallback: MentionCandidate = {
                id: nodeId,
                type: 'unknown',
                label: nodeId.slice(0, 8) + '...',
                icon: '',
              }
              const chip = createMentionChipElement(fallback, projectId)
              chip.className = chip.className
                .replace('border-primary/25', 'border-white/10')
                .replace('bg-primary/15', 'bg-white/5')
                .replace('text-primary', 'text-foreground/40')
              frag.appendChild(chip)
            }
            lastIndex = match.index + match[0].length
          }
          if (lastIndex < text.length) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex)))
          }

          // Track the last node for cursor placement (insertNode empties the fragment)
          const lastChild = frag.lastChild

          range.insertNode(frag)

          // Move cursor after the last inserted node
          if (lastChild) {
            range.setStartAfter(lastChild)
            range.collapse(true)
          } else {
            range.collapse(false)
          }
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // Plain text paste — insert as text node
          const textNode = document.createTextNode(text)
          range.insertNode(textNode)
          range.setStartAfter(textNode)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
        }

        emitContentState()
        adjustHeight()
      },
      [adjustHeight, candidates, emitContentState, projectId]
    )

    const removeAdjacentChip = useCallback(
      (direction: 'backward' | 'forward') => {
        const editor = editorRef.current
        if (!editor) return false

        const selection = window.getSelection()
        if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
          return false
        }

        const range = selection.getRangeAt(0)
        if (!range.collapsed) return false

        let adjacentNode: globalThis.Node | null = null
        if (range.startContainer instanceof Text) {
          const textNode = range.startContainer
          if (direction === 'backward' && range.startOffset === 0) {
            adjacentNode = textNode.previousSibling
          } else if (
            direction === 'forward' &&
            range.startOffset === (textNode.textContent ?? '').length
          ) {
            adjacentNode = textNode.nextSibling
          }
        } else if (range.startContainer instanceof HTMLElement) {
          const container = range.startContainer
          const childIndex =
            direction === 'backward' ? range.startOffset - 1 : range.startOffset
          adjacentNode = container.childNodes[childIndex] ?? null
        }

        if (
          adjacentNode instanceof HTMLElement &&
          adjacentNode.dataset.mentionPill === 'true'
        ) {
          removeChip(adjacentNode)
          return true
        }

        return false
      },
      [removeChip]
    )

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        // Dropdown keyboard nav
        if (showDropdown && filteredCandidates.length > 0) {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlightIndex((prev) => (prev + 1) % filteredCandidates.length)
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlightIndex((prev) =>
              (prev - 1 + filteredCandidates.length) % filteredCandidates.length
            )
            return
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            insertMention(filteredCandidates[highlightIndex])
            return
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            closeDropdown()
            return
          }
        }

        // Chip removal via Backspace/Delete
        if (event.key === 'Backspace' && removeAdjacentChip('backward')) {
          event.preventDefault()
          return
        }
        if (event.key === 'Delete' && removeAdjacentChip('forward')) {
          event.preventDefault()
          return
        }

        // Cmd/Ctrl+Z (no shift, no dropdown) — caller-supplied undo hook.
        // Used by callers that mutate the editor via setText() (innerHTML),
        // which bypasses the browser's native contentEditable undo stack.
        if (
          onUndo &&
          (event.metaKey || event.ctrlKey) &&
          !event.shiftKey &&
          !event.altKey &&
          event.key.toLowerCase() === 'z' &&
          !showDropdown
        ) {
          event.preventDefault()
          onUndo()
          return
        }

        if (
          event.key === 'Enter' &&
          event.shiftKey &&
          !showDropdown &&
          onSubmit &&
          submitOnShiftEnter
        ) {
          enterKeyLog({
            event: 'richtextbox-shift-enter-submit',
            submitOnEnter,
            submitOnShiftEnter,
            containerTestId: containerTestId ?? '',
            editorTestId,
          })
          event.preventDefault()
          onSubmit()
          return
        }

        // Submit on Enter for composer-like callers; preserve legacy Enter
        // suppression for callers that do not provide a submit hook.
        if (event.key === 'Enter' && !event.shiftKey && !showDropdown) {
          if (onSubmit) {
            enterKeyLog({
              event: submitOnEnter
                ? 'richtextbox-enter-submit'
                : 'richtextbox-enter-newline',
              submitOnEnter,
              submitOnShiftEnter,
              containerTestId: containerTestId ?? '',
              editorTestId,
            })
            if (!submitOnEnter) return
            event.preventDefault()
            onSubmit()
            return
          }
          event.preventDefault()
          return
        }

        // Escape (no dropdown)
        if (event.key === 'Escape' && !showDropdown) {
          event.preventDefault()
          onEscape?.()
        }
      },
      [
        closeDropdown,
        filteredCandidates,
        highlightIndex,
        insertMention,
        onEscape,
        onSubmit,
        onUndo,
        removeAdjacentChip,
        showDropdown,
        submitOnEnter,
        submitOnShiftEnter,
        containerTestId,
        editorTestId,
      ]
    )

    // ── Click handling for chips ──

    const handleEditorClick = useCallback(
      (event: MouseEvent) => {
        if (!(event.target instanceof HTMLElement)) return
        const target = event.target

        // Remove button click
        const removeButton = target.closest('[data-mention-chip-remove]')
        if (removeButton) {
          event.preventDefault()
          event.stopPropagation()
          const chip = removeButton.closest('[data-mention-pill]')
          if (chip instanceof HTMLElement) removeChip(chip)
          return
        }

        // Chip label click — pan to node
        const chipLabel = target.closest('[data-mention-chip-label]')
        if (chipLabel) {
          const chip = chipLabel.closest('[data-mention-pill]')
          if (chip instanceof HTMLElement && chip.dataset.nodeId) {
            event.preventDefault()
            event.stopPropagation()
            onMentionClick?.(chip.dataset.nodeId)
          }
        }
      },
      [onMentionClick, removeChip]
    )

    // ── Imperative handle ──

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          const editor = editorRef.current
          if (!editor) return
          editor.innerHTML = ''
          closeDropdown()
          emitContentState()
          adjustHeight()
        },
        focus: () => {
          const editor = editorRef.current
          if (!editor) return
          placeCaretAtEnd(editor)
        },
        setText: (text: string) => {
          const editor = editorRef.current
          if (!editor) return
          // Hydrate @[[nodeId]] markers
          hydrateContent(editor, text, candidates, projectId)
          emitContentState()
          adjustHeight()
        },
        getPlainText: () => getPlainTextFromEditor(editorRef.current),
        getReferencedNodeIds: () => getReferencedNodeIdsFromEditor(editorRef.current),
        insertAtCursor: (text: string) => {
          const editor = editorRef.current
          if (!editor) return
          editor.focus()
          const selection = window.getSelection()
          if (!selection || !selectionInsideEditor(editor, selection) || selection.rangeCount === 0) {
            placeCaretAtEnd(editor)
          }
          const sel = window.getSelection()
          if (!sel || sel.rangeCount === 0) return
          const range = sel.getRangeAt(0)

          if (!range.collapsed) {
            range.deleteContents()
          }

          const textNode = document.createTextNode(text)
          range.insertNode(textNode)

          range.setStartAfter(textNode)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)

          emitContentState()
          adjustHeight()
        },
        wrapSelection: (before: string, after: string) => {
          const editor = editorRef.current
          if (!editor) return
          editor.focus()
          const selection = window.getSelection()
          if (!selection || !selectionInsideEditor(editor, selection) || selection.rangeCount === 0) {
            placeCaretAtEnd(editor)
            const sel2 = window.getSelection()
            if (!sel2 || sel2.rangeCount === 0) return
            const range = sel2.getRangeAt(0)
            const textNode = document.createTextNode(before + after)
            range.insertNode(textNode)
            const cursorRange = document.createRange()
            cursorRange.setStart(textNode, before.length)
            cursorRange.collapse(true)
            sel2.removeAllRanges()
            sel2.addRange(cursorRange)
            emitContentState()
            adjustHeight()
            return
          }
          const range = selection.getRangeAt(0)

          if (range.collapsed) {
            const textNode = document.createTextNode(before + after)
            range.insertNode(textNode)
            const cursorRange = document.createRange()
            cursorRange.setStart(textNode, before.length)
            cursorRange.collapse(true)
            selection.removeAllRanges()
            selection.addRange(cursorRange)
          } else {
            const fragment = range.extractContents()
            const beforeNode = document.createTextNode(before)
            const afterNode = document.createTextNode(after)

            const wrapper = document.createDocumentFragment()
            wrapper.appendChild(beforeNode)
            wrapper.appendChild(fragment)
            wrapper.appendChild(afterNode)

            range.insertNode(wrapper)

            const cursorRange = document.createRange()
            cursorRange.setStartBefore(afterNode)
            cursorRange.collapse(true)
            selection.removeAllRanges()
            selection.addRange(cursorRange)
          }

          emitContentState()
          adjustHeight()
        },
      }),
      [adjustHeight, candidates, closeDropdown, emitContentState, projectId]
    )

    // ── Hydration of initial content ──

    useEffect(() => {
      const editor = editorRef.current
      if (!editor || !initialContent || initialContentHydratedRef.current) return
      // Wait for candidates if content has markers and canvasNodes not yet loaded
      const hasMarkers = /@\[\[[^\]]+\]\]/.test(initialContent)
      if (hasMarkers && canvasNodes === undefined) return
      initialContentHydratedRef.current = true

      hydrateContent(editor, initialContent, candidates, projectId)
      emitContentState()
      adjustHeight()
    }, [initialContent, canvasNodes, candidates, emitContentState, adjustHeight, projectId])

    useEffect(() => {
      const editor = editorRef.current
      if (!editor || !initialContentHydratedRef.current) return
      reconcileMentionChips(editor, candidates, projectId)
      adjustHeight()
    }, [adjustHeight, candidates, projectId])

    // ── Event listeners ──

    useEffect(() => {
      const handleDocumentClick = (event: MouseEvent) => {
        const editor = editorRef.current
        const dropdown = dropdownRef.current
        if (!(event.target instanceof globalThis.Node)) return
        const target = event.target
        if (!editor) return
        if (!editor.contains(target) && !(dropdown && dropdown.contains(target))) {
          closeDropdown()
        }
      }

      const editor = editorRef.current
      if (!editor) return

      editor.addEventListener('click', handleEditorClick)
      document.addEventListener('mousedown', handleDocumentClick)
      return () => {
        editor.removeEventListener('click', handleEditorClick)
        document.removeEventListener('mousedown', handleDocumentClick)
      }
    }, [closeDropdown, handleEditorClick])

    // Scroll highlighted item into view
    useEffect(() => {
      if (!showDropdown || !dropdownRef.current) return
      const items = dropdownRef.current.querySelectorAll('[data-mention-item]')
      items[highlightIndex]?.scrollIntoView({ block: 'nearest' })
    }, [highlightIndex, showDropdown])

    // `cursor-text` on the container is load-bearing, not cosmetic: ReactFlow
    // sets `cursor: grab` on `.react-flow__node` and `cursor` inherits, so every
    // text box inside a canvas node showed an open-hand pointer as if the field
    // were draggable. It goes on the CONTAINER rather than the editor because
    // the padded area around the text is part of the visible field and focuses
    // it on click, so it has to read as editable too.
    //
    // Left inherited when `disabled`: the box is not editable then, and the node
    // underneath still is draggable, so `grab` is the honest cursor there.
    return (
      <div className={`relative flex flex-col ${disabled ? '' : 'cursor-text'} ${flexFill ? 'flex-1 min-h-0' : ''} ${containerClassName ?? ''}`} data-testid={containerTestId}>
        {/* Dropdown — rendered via portal to avoid clipping by overflow containers */}
        {showDropdown && filteredCandidates.length > 0 && dropdownPos && createPortal(
          <div
            ref={dropdownRef}
            data-rich-text-dropdown
            className="fixed z-[9999] max-h-[200px] overflow-y-auto rounded-lg border border-border-subtle bg-surface-1 shadow-lg"
            style={{
              top: dropdownPos.top - 4,
              left: dropdownPos.left,
              width: dropdownPos.width,
              transform: 'translateY(-100%)',
            }}
            role="listbox"
            data-testid="mention-dropdown"
          >
            {filteredCandidates.map((candidate, index) => (
              <React.Fragment key={candidate.id}>
              {mentionSections.sectioned && (index === 0 || index === mentionSections.referenceCount) && <div role="presentation" className="px-3 py-1.5 text-xs font-medium text-text-muted" data-testid="mention-section-header">{index < mentionSections.referenceCount ? t('referenceInteraction.referenceSection') : t('referenceInteraction.canvasSection')}</div>}
              <DropdownItem
                candidate={candidate}
                isHighlighted={index === highlightIndex}
                projectId={projectId}
                onSelect={insertMention}
                onMouseEnter={() => setHighlightIndex(index)}
              />
              </React.Fragment>
            ))}
          </div>,
          document.body
        )}

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable={disabled ? 'false' : 'true'}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-readonly={disabled}
          data-testid={editorTestId}
          data-placeholder={placeholder}
          className={`rich-text-box__editor w-full flex-1 min-h-0 overflow-y-auto whitespace-pre-wrap break-words bg-transparent text-sm text-foreground focus:outline-none pb-5 select-text ${
            disabled ? 'opacity-50' : ''
          } ${className}`}
          style={flexFill ? { userSelect: 'text', WebkitUserSelect: 'text' } : { minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px`, userSelect: 'text', WebkitUserSelect: 'text' }}
          onInput={disabled ? undefined : handleInput}
          onKeyDown={disabled ? undefined : handleKeyDown}
          onCopy={handleCopy}
          onCut={disabled ? undefined : handleCut}
          onPaste={disabled ? undefined : handlePaste}
          onCompositionStart={disabled ? undefined : handleCompositionStart}
          onCompositionEnd={disabled ? undefined : handleCompositionEnd}
        />

        <style>{`
          .rich-text-box__editor:empty::before {
            content: attr(data-placeholder);
            color: var(--text-muted, #6a6a8a);
            pointer-events: none;
          }
        `}</style>

        {/* Built-in character counter */}
        {maxLength > 0 && (
          <div
            className={`absolute bottom-0 right-0 text-xxs tabular-nums ${counterClassName} pl-1 ${
              textLength >= maxLength ? 'text-red-400' : 'text-text-muted/60'
            }`}
            data-testid="rich-text-char-counter"
          >
            {textLength} / {maxLength}
          </div>
        )}
      </div>
    )
  }
)

const RichTextBox = memo(RichTextBoxInner)
RichTextBox.displayName = 'RichTextBox'

/**
 * Canvas-bound RichTextBox that subscribes to ReactFlow nodes at the editor
 * boundary. Keeping this subscription here lets mention chips reconcile when
 * a referenced node is added, removed, or renamed without forcing the owning
 * canvas card to subscribe to every node mutation.
 *
 * RichTextBox remains provider-agnostic for non-canvas consumers such as the
 * landing-page composer.
 */
const CanvasRichTextBoxInner = forwardRef<RichTextBoxHandle, CanvasRichTextBoxProps>(
  function CanvasRichTextBox(
    {
      referenceOwnerNodeId,
      referenceOwnerNodeType,
      ...richTextBoxProps
    },
    ref,
  ) {
    if (!referenceOwnerNodeId.trim()) {
      throw new Error('CanvasRichTextBox requires a non-empty referenceOwnerNodeId')
    }
    const canvasNodes = useNodes<ReactFlowCanvasNodeData>()

    return (
      <RichTextBox
        ref={ref}
        {...richTextBoxProps}
        canvasNodes={canvasNodes}
        referenceOwnerNodeId={referenceOwnerNodeId}
        referenceOwnerNodeType={referenceOwnerNodeType}
      />
    )
  },
)

const CanvasRichTextBox = memo(CanvasRichTextBoxInner)
CanvasRichTextBox.displayName = 'CanvasRichTextBox'

export { CanvasRichTextBox, RichTextBox }
